import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { Gift, CheckCircle2, Phone, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = Array.from({ length: 22 }, (_, i) => { const h = 8 + Math.floor(i / 2); const m = i % 2 ? '30' : '00'; return `${String(h).padStart(2, '0')}:${m}`; });
const label12 = (t) => { const [h, m] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`; };

const Shell = ({ children }) => (
  <div className="min-h-screen bg-background flex items-center justify-center p-4">
    <div className="w-full max-w-lg rounded-2xl border border-primary/40 bg-card/90 p-6 sm:p-8 space-y-5 shadow-2xl" data-testid="referral-page">
      <p className="text-[10px] uppercase tracking-[0.3em] text-primary flex items-center gap-2"><Sparkles className="w-4 h-4" />EliteIV · Friends of Elite Members</p>
      {children}
    </div>
  </div>
);

export default function ReferralPage() {
  const { code } = useParams();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState('');
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', date: '', time: '10:00', service_type_id: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    axios.get(`${API}/public/referral/${code}`).then(r => setOffer(r.data)).catch(e => setError(e.response?.data?.detail || "This referral link isn't valid"));
    axios.get(`${API}/public/services`).then(r => { const iv = r.data.filter(s => ['iv_drip', 'hydration'].includes(s.category)); setServices(iv); if (iv[0]) setForm(f => ({ ...f, service_type_id: iv[0].id })); }).catch(() => {});
  }, [code]);

  const submit = async () => {
    if (!form.first_name || !form.last_name) { setError('Please enter your name'); return; }
    if (!form.email && !form.phone) { setError('Please enter an email or phone number'); return; }
    if (!form.date) { setError('Please choose a date'); return; }
    setSubmitting(true); setError('');
    try {
      const r = await axios.post(`${API}/public/referral/${code}/book`, { first_name: form.first_name, last_name: form.last_name, email: form.email || null, phone: form.phone || null, requested_datetime: `${form.date}T${form.time}:00`, service_type_id: form.service_type_id || null, notes: form.notes || null });
      setDone(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Something went wrong — please call us.'); }
    finally { setSubmitting(false); }
  };

  if (error && !offer) return <Shell><p className="text-rose-300" data-testid="referral-error">{error}</p><p className="text-sm text-muted-foreground flex items-center gap-2"><Phone className="w-4 h-4" />Call or text 727-503-2572 and we'll take care of you.</p></Shell>;
  if (!offer) return <Shell><p className="text-muted-foreground text-sm">Loading…</p></Shell>;

  if (done) return (
    <Shell>
      <div className="text-center space-y-3" data-testid="referral-success">
        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
        <h1 className="text-2xl font-bold gold-gradient-text brand-heading">You're booked, {form.first_name}!</h1>
        <p className="text-sm text-muted-foreground">Requested <b className="text-foreground">{format(parseISO(done.start_datetime), 'EEEE, MMMM d · h:mm a')}</b> · {done.service}. Your <b className="text-foreground">${Number(done.discount).toFixed(0)} off</b> is applied at checkout. We'll confirm shortly!</p>
      </div>
    </Shell>
  );

  if (!offer.promo_active) return (
    <Shell>
      <div className="space-y-3" data-testid="referral-inactive">
        <h1 className="text-2xl font-bold gold-gradient-text brand-heading">{offer.member_first_name} thinks you'd love EliteIV</h1>
        <p className="text-sm text-muted-foreground">Our referral promotion isn't running right now, but we'd still love to see you. Call or text <b className="text-foreground">727-503-2572</b> to book your first mobile IV visit.</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold gold-gradient-text brand-heading">{offer.member_first_name} is treating you to ${Number(offer.friend_discount).toFixed(0)} off</h1>
        <p className="text-sm text-muted-foreground mt-2">Book your first EliteIV mobile IV visit below — we come to you. The discount is applied when you check out.</p>
      </div>
      <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/15 to-transparent p-3 flex items-center gap-3">
        <Gift className="w-8 h-8 text-primary shrink-0" />
        <div className="text-sm"><b className="text-foreground">${Number(offer.friend_discount).toFixed(0)} off your first visit</b><p className="text-xs text-muted-foreground">First-time patients only{offer.ends_at ? ` · offer ends ${format(parseISO(offer.ends_at), 'MMM d')}` : ''}</p></div>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">First name</Label><Input value={form.first_name} onChange={set('first_name')} data-testid="ref-first-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Last name</Label><Input value={form.last_name} onChange={set('last_name')} data-testid="ref-last-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={set('email')} data-testid="ref-email" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input type="tel" value={form.phone} onChange={set('phone')} data-testid="ref-phone" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" min={format(new Date(), 'yyyy-MM-dd')} value={form.date} onChange={set('date')} data-testid="ref-date" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Time</Label>
            <Select value={form.time} onValueChange={set('time')}><SelectTrigger data-testid="ref-time"><SelectValue /></SelectTrigger><SelectContent>{TIMES.map(t => <SelectItem key={t} value={t}>{label12(t)}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        {services.length > 0 && (
          <div className="space-y-1.5"><Label className="text-xs">Which drip?</Label>
            <Select value={form.service_type_id} onValueChange={set('service_type_id')}><SelectTrigger data-testid="ref-service"><SelectValue /></SelectTrigger><SelectContent>{services.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · ${Number(s.price).toFixed(0)}</SelectItem>)}</SelectContent></Select>
          </div>
        )}
        <div className="space-y-1.5"><Label className="text-xs">Anything we should know? (optional)</Label><Textarea rows={2} value={form.notes} onChange={set('notes')} data-testid="ref-notes" /></div>
        {error && <p className="text-sm text-rose-300" data-testid="referral-error">{error}</p>}
        <Button onClick={submit} disabled={submitting} className="w-full h-12 text-base bg-primary text-primary-foreground" data-testid="ref-submit"><Gift className="w-5 h-5 mr-2" />{submitting ? 'Sending…' : `Book my first visit · $${Number(offer.friend_discount).toFixed(0)} off`}</Button>
        <p className="text-[11px] text-muted-foreground text-center">Prefer to talk? Call or text 727-503-2572.</p>
      </div>
    </Shell>
  );
}
